import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, Select, Button, message, Tooltip, Drawer, Divider,
  Dropdown, Space, Modal, Form, Upload, AutoComplete,
} from "antd";
import {
  SearchOutlined, UserOutlined, EyeOutlined, PhoneOutlined,
  ReloadOutlined, FileExcelOutlined, FilePdfOutlined, DownOutlined,
  MailOutlined, CalendarOutlined, IdcardOutlined, EnvironmentOutlined,
  CheckCircleOutlined, ClockCircleOutlined, WhatsAppOutlined, CopyOutlined,
  CrownOutlined, WalletOutlined, UserAddOutlined, LockOutlined,
  CameraOutlined, HeartFilled, GiftOutlined, SafetyOutlined,
} from "@ant-design/icons";
import {
  UserGet, UserPost, UserDataPostwt,
  User_StateGet, User_CasteGet,
  getDistrictsByState, getCitiesByDistrict, getCastesByReligion,
  getReligionOptions,
} from "../../../../Api/CoreApi";
import { BASE_API } from "../../../../Api/Api"
import { exportToCSV } from "../../../Admin_Panel/Utils/exportCSV";
import { exportToPDF } from "../../../Admin_Panel/Utils/exportPDF";
import { formatDate, capitalize, formatCurrency } from "../../../Admin_Panel/Utils/formatters";
import {
  religions, Hindu, Muslim, MuslimShia, MuslimSunni,
  Christian, Sikh, JainAll, JainDigambar, JainShwetambar,
  Parsi, Buddhist, Jewish, InterReligion, NoReligiousBelief
} from 'indian-religion-caste';

import "./StaffUsers.css";

const { Option } = Select;
const generateHeightOptions = () => {
  const heights = [];
  for (let feet = 4; feet <= 8; feet++) {
    const maxInches = feet === 8 ? 0 : 11;
    for (let inches = 0; inches <= maxInches; inches++) {
      const totalCm = Math.round((feet * 30.48) + (inches * 2.54));
      heights.push(`${feet}'${inches}" (${totalCm} cm)`);
    }
  }
  return heights;
};

const HEIGHT_OPTIONS = generateHeightOptions();

const occupationOptions = [
  "Government Employee", "Private Employee", "Business",
  "Self-Employed", "Farmer", "Teacher", "Doctor", "Engineer",
  "Lawyer", "Architect", "Banker", "CA", "Professor",
  "Retired", "Housewife", "Other"
];
const fatherOccupationOptions = occupationOptions.filter(
  option => option !== "Housewife"
);
const subCourseData = {
  "B.Sc": ["Physics", "Chemistry", "Mathematics", "Biology"],
  "M.Sc": ["Physics", "Chemistry", "Mathematics"],
  "B.Tech": ["Computer Science", "Electronics", "Mechanical"],
  "MBBS": ["General Medicine", "Pediatrics"],
  "B.Com": ["Accountancy", "Finance"],
  "MBA": ["Marketing", "Finance", "HR"],
  "BCA": ["Web Development", "App Development"],
  "MCA": ["Data Science", "AI & ML"],
  "10th": ["Science", "Commerce", "Arts"],
  "12th": ["Science", "Commerce", "Arts"],
  "Other": ["Other"]
};

// ✅ AutoComplete + Custom Input Component
const SelectOrInput = ({ options = [], placeholder = "Select or type...", value, onChange, prefix, disabled = false }) => {
  const autoOptions = options.map(opt => ({
    value: typeof opt === 'object' ? (opt.name || opt.value || opt) : opt,
    label: typeof opt === 'object' ? (opt.name || opt.label || opt) : opt,
  }));

  return (
    <AutoComplete
      options={autoOptions}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      allowClear
      filterOption={(inputValue, option) =>
        option?.value?.toLowerCase().includes(inputValue?.toLowerCase())
      }
      style={{ width: "100%" }}
    >
      <Input prefix={prefix} placeholder={placeholder} />
    </AutoComplete>
  );
};

function StaffUsers() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [form] = Form.useForm();
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCastes, setFilteredCastes] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [subCourses, setSubCourses] = useState([]);
  const [isOtherCourse, setIsOtherCourse] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [religionOptions, setReligionOptions] = useState([]);
  const staffUserId = localStorage.getItem("user_id");
  const staffId = localStorage.getItem("staff_id") || "";
  const staffName = localStorage.getItem("username") || "Staff";
  const staffRef = localStorage.getItem("refer") || "";
  const staffProfileId = localStorage.getItem("profile_id") || "";


  const getProfileId = (user) => {
    if (!user) return 'N/A';
    return user.profile_id || user.user?.profile_id || `CRUS${String(user.id || 0).padStart(6, '0')}`;
  };

  const isAddedByStaff = (user) => {
    if (!user) return false;
    if (user.added_by_staff && String(user.added_by_staff) === String(staffUserId)) {
      return true;
    }
    return false;
  };

  const isReferralUser = (user) => {
    if (!user) return false;
    if (user.added_by_staff && String(user.added_by_staff) === String(staffUserId)) {
      return false;
    }
    const staffRefStr = String(staffRef || '').trim();
    const staffPidStr = String(staffProfileId || '').trim();
    const userReferBy = String(user.refer_by || '').trim();
    if (userReferBy && staffRefStr && userReferBy === staffRefStr) return true;
    const userRef = String(user.ref || '').trim();
    if (userRef && staffRefStr && userRef === staffRefStr) return true;
    if (userRef && staffPidStr && userRef === staffPidStr) return true;
    return false;
  };

  const belongsToStaff = (user) => isAddedByStaff(user) || isReferralUser(user);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await UserGet();
      const allUsersOnly = Array.isArray(res) ? res.filter(u => u.role === "user") : [];
      const staffUsers = allUsersOnly.filter(belongsToStaff);
      setUsers(staffUsers);
      setFiltered(staffUsers);
    } catch {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    User_StateGet().then(res => setStates(res || [])).catch(() => { });
    getReligionOptions()
      .then(res => setReligionOptions(res.religions || []))
      .catch(() => setReligionOptions([]));
  }, []);

  useEffect(() => {
    let f = [...users];
    if (searchText) {
      const s = searchText.toLowerCase();
      f = f.filter(u =>
        u.first_name?.toLowerCase().includes(s) || u.username?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s) || u.contact?.includes(searchText) ||
        getProfileId(u).toLowerCase().includes(s)
      );
    }
    if (statusFilter !== "all") {
      f = f.filter(u => {
        const active = u.is_active || u.status === "active";
        return statusFilter === "active" ? active : statusFilter === "inactive" ? !active : statusFilter === "premium" ? (u.is_premium || u.premium) : true;
      });
    }
    if (sourceFilter === "added") f = f.filter(isAddedByStaff);
    if (sourceFilter === "referral") f = f.filter(isReferralUser);
    setFiltered(f);
  }, [searchText, statusFilter, sourceFilter, users]);

  const handleStateChange = async (value) => {
    setDistricts([]); setCities([]);
    form.setFieldsValue({ district: undefined, city: undefined });
    if (!value) return;
    try {
      const res = await getDistrictsByState(value);
      setDistricts(res?.districts || (Array.isArray(res) ? res : []));
    } catch { setDistricts([]); }
  };

  const handleDistrictChange = async (value) => {
    setCities([]);
    form.setFieldsValue({ city: undefined });
    if (!value) return;
    try {
      const res = await getCitiesByDistrict(value);
      setCities(res?.cities || (Array.isArray(res) ? res : []));
    } catch { setCities([]); }
  };

  const handleReligionChange = (value) => {
    setFilteredCastes([]);
    form.setFieldsValue({ caste: undefined, subcaste: undefined });
    if (!value) return;

    const casteMap = {
      'Hindu': Hindu,
      'Muslim': MuslimShia,
      'MuslimShia': MuslimShia,
      'MuslimSunni': MuslimSunni,
      'Christian': Christian,
      'Sikh': Sikh,
      'Jain': JainDigambar,
      'JainDigambar': JainDigambar,
      'JainShwetambar': JainShwetambar,
      'Buddhist': Buddhist,
      'Parsi': Parsi,
      'Jewish': Jewish,
      'InterReligion': InterReligion,
      'NoReligiousBelief': NoReligiousBelief,
    };

    setFilteredCastes(casteMap[value] || []);
  };

  const handleCourseChange = (value) => {
    setSelectedCourse(value);
    form.setFieldsValue({ sub_course: undefined });
    if (value === 'Other') { setIsOtherCourse(true); setSubCourses([]); return; }
    setIsOtherCourse(false);
    setSubCourses(subCourseData[value] || []);
  };

  const handleCall = (c) => c ? (window.location.href = `tel:${c}`) : message.warning("No contact");
  const handleWhatsApp = (c) => c ? window.open(`https://wa.me/${c.replace(/\D/g, "")}`) : message.warning("No contact");
  const handleCopy = (text, label) => { if (text) { navigator.clipboard.writeText(text); message.success(`${label} copied`); } };

  const handleAddUser = async (values) => {
    try {
      setAddLoading(true);
      const cleanData = {
        username: values.username.trim(),
        password: values.password,
        email: values.email || "",
        first_name: values.firstname || "",
        contact: values.contact || "",
        gender: values.gender || "",
        state: values.state || "",
        district: values.district || "",
        role: "user",
        added_by_staff: staffUserId,
        added_by_type: "staff",
        added_method: "direct",
      };

      let newUserId = null;

      try {
        const userData = await UserPost(cleanData);

        if (!userData || !userData.user_id) {
          message.success("✅ User created!");
          handleModalClose();
          fetchUsers();
          return;
        }

        newUserId = userData.user_id;

        let age = null;
        if (values.dob) {
          const d = new Date(values.dob);
          const t = new Date();
          age = t.getFullYear() - d.getFullYear();
          const m = t.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age--;
        }

        try {
          const profileData = new FormData();
          profileData.append("user_id", newUserId);
          profileData.append("username", values.username.trim());
          profileData.append("role", "user");

          const textFields = [
            "firstname", "gender", "state", "city",
            "caste", "subcaste", "religion", "email", "contact",
            "contact1", "dob", "father_name", "mother_name",
            "father_occupation", "mother_occupation", "university",
            "course", "sub_course", "job_title", "job_type",
            "Lifestyle", "marrige_status", "mother_tongue",
            "height", "blood_group", "created_profile",
            "food_lifestyle", "additional_notes",
          ];

          textFields.forEach((key) => {
            const val = values[key];
            if (val !== undefined && val !== null && val !== "") {
              profileData.append(key, val);
            }
          });

          const district = values.district || values.disttrict || "";
          if (district) profileData.append("district", district);
          profileData.append("country", values.country || "India");
          if (age !== null) profileData.append("age", String(age));

          ["brother", "brother_marrige", "sister", "sister_marrige"].forEach((key) => {
            if (values[key] !== undefined && values[key] !== null) {
              profileData.append(key, String(values[key]));
            } else {
              profileData.append(key, "0");
            }
          });

          if (values.annual_income) profileData.append("annual_income", String(values.annual_income));
          if (staffUserId) profileData.append("added_by_staff", staffUserId);
          if (staffRef) profileData.append("refer_by", staffRef);
          profileData.append("added_by_type", "staff");
          profileData.append("added_method", "direct");
          if (file) profileData.append("pic", file);

          await UserDataPostwt(profileData);

        } catch (profileError) {
          console.warn("⚠️ Profile save failed:", profileError);
          message.warning("User created but profile save failed. Edit later.");
          handleModalClose();
          fetchUsers();
          return;
        }

        message.success("✅ User added successfully!");
        handleModalClose();
        fetchUsers();

      } catch (error) {
        const errorMsg =
          error?.response?.data?.username?.[0] ||
          error?.response?.data?.email?.[0] ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message || "Failed to add user";

        if (errorMsg.includes("username") || errorMsg.includes("already")) {
          message.error("Username already exists!");
        } else {
          message.error(errorMsg);
        }
      }

    } catch (error) {
      const errorMsg =
        error?.response?.data?.username?.[0] ||
        error?.response?.data?.email?.[0] ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message || "Failed to add user";

      if (errorMsg.includes("username") || errorMsg.includes("already")) {
        message.error("Username already exists!");
      } else {
        message.error(errorMsg);
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleModalClose = () => {
    setAddModalOpen(false); form.resetFields();
    setFile(null); setPreviewUrl(null);
    setDistricts([]); setCities([]); setFilteredCastes([]);
    setSubCourses([]); setSelectedCourse(''); setIsOtherCourse(false);
  };

  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "profile_id", label: "Profile ID", format: r => getProfileId(r) },
    { key: "source", label: "Source", format: r => isAddedByStaff(r) ? "Added" : "Referral" },
    { key: "name", label: "Name", format: r => capitalize(r.first_name) || r.username || "N/A" },
    { key: "contact", label: "Mobile", format: r => r.contact || "N/A" },
    { key: "email", label: "Email", format: r => r.email || "N/A" },
    { key: "status", label: "Status", format: r => r.is_active ? "Active" : "Inactive" },
    { key: "date", label: "Created", format: r => formatDate(r.date_joined) },
  ];

  const csvMenu = {
    items: [
      { key: "1", label: `Filtered (${filtered.length})`, onClick: () => exportToCSV(filtered, exportColumns, "staff_users") },
      { key: "2", label: `All (${users.length})`, onClick: () => exportToCSV(users, exportColumns, "staff_users") },
    ]
  };

  const pdfMenu = {
    items: [
      {
        key: "1", label: `Filtered (${filtered.length})`,
        onClick: () => exportToPDF(filtered, exportColumns, {
          title: "Staff Users Report", subtitle: "Users managed by staff",
          filename: "staff_users", orientation: "landscape",
        }),
      },
      {
        key: "2", label: `All (${users.length})`,
        onClick: () => exportToPDF(users, exportColumns, {
          title: "Staff Users Report", subtitle: "All users managed by staff",
          filename: "staff_users_all", orientation: "landscape",
        }),
      },
    ],
  };
  // StaffUsers component ke andar add karo
  const getReligionIcon = (religion) => {
    const icons = {
      'Hindu': '🕉️', 'Muslim': '🕌', 'MuslimShia': '🕌',
      'MuslimSunni': '🕌', 'Christian': '⛪', 'Sikh': '🪯',
      'Jain': '✋', 'JainDigambar': '✋', 'JainShwetambar': '✋',
      'Buddhist': '☸️', 'Parsi': '🔥', 'Jewish': '✡️',
      'InterReligion': '🤝', 'NoReligiousBelief': '🌿'
    };
    return icons[religion] || '🔯';
  };
  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active || u.status === "active").length,
    premium: users.filter(u => u.is_premium || u.premium).length,
    added: users.filter(isAddedByStaff).length,
    referral: users.filter(isReferralUser).length,
  };

  const columns = [
    { title: "#", key: "sno", width: 45, fixed: "left", render: (_, __, i) => <span className="su-sno">{i + 1}</span> },
    { title: "Profile ID", key: "pid", width: 120, render: (_, r) => <span className="su-ref">{getProfileId(r)}</span> },
    {
      title: "User Name", key: "user", width: 180,
      render: (_, r) => (
        <div className="su-name-cell">
          <div className="su-avatar">{(r.first_name?.[0] || r.username?.[0] || "U").toUpperCase()}</div>
          <div className="su-name-info">
            <div className="su-name">{capitalize(r.first_name) || r.username || "N/A"}</div>
            <div className="su-source-badge">
              {isAddedByStaff(r) ? <Tag color="blue">👤 Added</Tag> : isReferralUser(r) ? <Tag color="purple"><GiftOutlined /> Referral</Tag> : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Mobile / Call", key: "mobile", width: 130,
      render: (_, r) => (
        <div className="su-mobile-cell">
          <span className="su-mobile"><PhoneOutlined /> {r.contact || "N/A"}</span>
          {r.contact && <Tooltip title={`Call ${r.contact}`}></Tooltip>}
        </div>
      ),
    },
    { title: "Email", dataIndex: "email", key: "email", width: 200, render: v => <span className="su-email-cell">{v || "N/A"}</span> },
    {
      title: "Plan", key: "plan", width: 100,
      render: (_, r) => <Tag className={`su-plan-tag ${(r.is_premium || r.premium) ? "premium" : "free"}`}>{(r.is_premium || r.premium) ? <><CrownOutlined /> Premium</> : "Free"}</Tag>,
    },
    {
      title: "Status", key: "status", width: 100,
      render: (_, r) => {
        const active = r.is_active || r.status === "active";
        return <Tag className={`su-status-tag ${active ? "active" : "inactive"}`}>{active ? <><CheckCircleOutlined /> Active</> : <><ClockCircleOutlined /> Inactive</>}</Tag>;
      },
    },
    { title: "Created", key: "date", width: 100, render: (_, r) => <span className="su-date">{formatDate(r.date_joined)}</span> },
    {
      title: "Actions", key: "action", width: 110, fixed: "right",
      render: (_, r) => (
        <Space size={3}>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} className="su-action-btn su-action-view" onClick={() => { setSelectedUser(r); setDrawerOpen(true); }} /></Tooltip>
          <Tooltip title="Call"><Button size="small" icon={<PhoneOutlined />} className="su-action-btn su-action-call" onClick={() => handleCall(r.contact)} /></Tooltip>
          <Tooltip title="WhatsApp"><Button size="small" icon={<WhatsAppOutlined />} className="su-action-btn su-action-whatsapp" onClick={() => handleWhatsApp(r.contact)} /></Tooltip>
        </Space>
      ),
    },
  ];

  // ✅ All dropdown options as arrays for AutoComplete
  const genderOptions = ["👨 Male", "👩 Female"];
  const maritalOptions = ["💑 Unmarried", "💔 Divorced", "🕊️ Widowed", "Awaited"];
  const heightOptions = HEIGHT_OPTIONS;
  const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const motherTongueOptions = ["Hindi", "English", "Marathi", "Punjabi", "Gujarati", "Bengali", "Tamil", "Telugu", "Kannada", "Malayalam", "Odia", "Urdu"];
  const foodOptions = ["🥗 Vegetarian", "🍗 Non-Vegetarian", "🥚 Eggetarian"];

  const courseOptions = ["10th", "12th", "B.Sc", "M.Sc", "B.Tech", "MBBS", "B.Com", "M.Com", "MBA", "BCA", "MCA", "BA", "MA", "B.Ed", "LLB", "Ph.D", "Diploma", "ITI", "Other"];
  const jobTypeOptions = ["🏛️ Government", "🏢 Private", "👤 Self Employed", "📭 Not Working"];
  const createdByOptions = ["👤 Self", "👨‍👩‍👦 Parent", "👫 Sibling", "🤝 Friend", "👤 Staff", "👥 Other"];

  return (
    <div className="su-page">
      {/* HEADER */}
      <div className="su-header">
        <div className="su-header-left">
          <div className="su-title-row">
            <div className="su-title-icon"><UserOutlined /></div>
            <div className="su-title-content">
              <h2 className="su-title">User Management</h2>
              <p className="su-subtitle">{filtered.length} of {users.length} users</p>
            </div>
          </div>
        </div>
        <div className="su-header-actions">
          <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading} className="su-icon-btn" /></Tooltip>
          <Dropdown menu={csvMenu} trigger={["click"]}>
            <Button icon={<FileExcelOutlined />} className="su-csv-btn">
              <Space size={4}>CSV<DownOutlined className="su-dropdown-arrow" /></Space>
            </Button>
          </Dropdown>
          <Dropdown menu={pdfMenu} trigger={["click"]}>
            <Button icon={<FilePdfOutlined />} className="su-pdf-btn">
              <Space size={4}>PDF<DownOutlined className="su-dropdown-arrow" /></Space>
            </Button>
          </Dropdown>
          <Button type="primary" icon={<UserAddOutlined />} className="su-add-btn" onClick={() => setAddModalOpen(true)}>Add User</Button>
        </div>
      </div>

      {/* STATS */}
      <div className="su-mini-stats">
        <div className="su-mini-stat"><UserOutlined className="su-mini-icon total" /><b>{stats.total}</b><span>Total Users</span></div>
        <div className="su-mini-divider" />
        <div className="su-mini-stat"><CheckCircleOutlined className="su-mini-icon active" /><b>{stats.active}</b><span>Active</span></div>
        <div className="su-mini-divider" />
        <div className="su-mini-stat"><CrownOutlined className="su-mini-icon premium" /><b>{stats.premium}</b><span>Premium</span></div>
        <div className="su-mini-divider" />
        <div className="su-mini-stat"><UserOutlined className="su-mini-icon added" /><b>{stats.added}</b><span>Added</span></div>
        <div className="su-mini-divider" />
        <div className="su-mini-stat"><GiftOutlined className="su-mini-icon referral" /><b>{stats.referral}</b><span>Referral</span></div>
      </div>

      {/* FILTERS */}
      <div className="su-filters">
        <Input placeholder="Search by name, profile ID, email, mobile..." prefix={<SearchOutlined />} value={searchText} onChange={e => setSearchText(e.target.value)} className="su-search" allowClear />
        <Select value={statusFilter} onChange={setStatusFilter} className="su-select">
          <Option value="all">All Users</Option>
          <Option value="active">Active Only</Option>
          <Option value="inactive">Inactive Only</Option>
          <Option value="premium">Premium Only</Option>
        </Select>
        <Select value={sourceFilter} onChange={setSourceFilter} className="su-select su-source-select">
          <Option value="all">All Sources</Option>
          <Option value="added">👤 Added by Staff</Option>
          <Option value="referral">🎁 Referral Users</Option>
        </Select>
      </div>

      {/* ===== TABLE (Desktop) ===== */}
<div className="su-table-card su-desktop-only">
  <Table 
    rowKey="id" 
    columns={columns} 
    dataSource={filtered} 
    loading={loading} 
    size="small"
    pagination={{ 
      pageSize: 10, 
      showSizeChanger: true, 
      size: "small", 
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users` 
    }}
    scroll={{ x: 1200 }} 
    className="su-table" 
  />
</div>
      {/* ===== MOBILE CARDS ===== */}
<div className="su-mobile-cards su-mobile-only">
  {filtered.length > 0 ? (
    filtered.map((user) => {
      const isActive = user.is_active || user.status === "active";
      const isPremium = user.is_premium || user.premium;
      const profileId = getProfileId(user);
      const initial = (user.first_name?.[0] || user.username?.[0] || "U").toUpperCase();
      const fullName = capitalize(user.first_name) || user.username || "N/A";
      const picUrl = user.pic
        ? (user.pic.startsWith("http") ? user.pic : `${BASE_API}${user.pic}`)
        : null;

      return (
        <div key={user.id} className="su-mcard">
          {/* Top: Profile ID + Status */}
          <div className="su-mcard-top">
            <span className="su-mcard-pid">{profileId}</span>
            <span className={`su-mcard-status ${isActive ? 'active' : 'inactive'}`}>
              ● {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Middle: Avatar + Info */}
          <div className="su-mcard-body">
            <div className="su-mcard-avatar-wrap">
              {picUrl ? (
                <img 
                  src={picUrl} 
                  alt={fullName} 
                  className="su-mcard-avatar-img"
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                    e.target.nextSibling.style.display = 'flex'; 
                  }}
                />
              ) : null}
              <div 
                className="su-mcard-avatar-fallback" 
                style={{ display: picUrl ? 'none' : 'flex' }}
              >
                {initial}
              </div>
            </div>

            <div className="su-mcard-info">
              <h3 className="su-mcard-name">{fullName}</h3>
              {user.contact && (
                <a 
                  href={`tel:${user.contact}`}
                  className="su-mcard-phone"
                  onClick={(e) => e.stopPropagation()}
                >
                  <PhoneOutlined /> {user.contact}
                </a>
              )}
              {isAddedByStaff(user) && (
                <span className="su-mcard-source added">👤 Added</span>
              )}
              {isReferralUser(user) && (
                <span className="su-mcard-source referral">🎁 Referral</span>
              )}
            </div>
          </div>

          {/* Bottom: Plan + Actions */}
          <div className="su-mcard-footer">
            <span className={`su-mcard-plan ${isPremium ? 'premium' : 'free'}`}>
              {isPremium ? <><CrownOutlined /> Premium</> : 'Free Plan'}
            </span>

            <div className="su-mcard-actions">
              <button 
                className="su-mcard-btn view" 
                onClick={() => { setSelectedUser(user); setDrawerOpen(true); }}
                title="View"
              >
                <EyeOutlined />
              </button>
           
              <button 
                className="su-mcard-btn call" 
                onClick={() => handleCall(user.contact)}
                title="Call"
              >
                <PhoneOutlined />
              </button>
              <button 
                className="su-mcard-btn whatsapp" 
                onClick={() => handleWhatsApp(user.contact)}
                title="WhatsApp"
              >
                <WhatsAppOutlined />
              </button>
            </div>
          </div>
        </div>
      );
    })
  ) : !loading && (
    <div className="su-empty">
      <UserOutlined className="su-empty-icon" />
      <h3>No Users Found</h3>
      <p>Try adjusting your search or filters</p>
    </div>
  )}
</div>

      {filtered.length === 0 && !loading && (
        <div className="su-empty"><UserOutlined className="su-empty-icon" /><h3>No Users Found</h3><p>Try adjusting your search or filters</p></div>
      )}

      {/* DRAWER */}
      {/* DRAWER - PROFESSIONAL USER DETAILS */}
      <Drawer
        title={null}
        placement="right"
        width={500}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className="su-drawer-pro"
        headerStyle={{ display: 'none' }}
        bodyStyle={{ padding: 0 }}
      >
        {selectedUser && (() => {
          const isActive = selectedUser.is_active || selectedUser.status === "active";
          const isPremium = selectedUser.is_premium || selectedUser.premium;
          const initial = (selectedUser.first_name?.[0] || selectedUser.username?.[0] || "U").toUpperCase();
          const fullName = capitalize(selectedUser.first_name) || selectedUser.username || "N/A";
          const profileId = getProfileId(selectedUser);
          const source = isAddedByStaff(selectedUser) ? "Added by Staff" : "Referral User";
          const sourceIcon = isAddedByStaff(selectedUser) ? "👤" : "🎁";
          const sourceColor = isAddedByStaff(selectedUser) ? "#3b82f6" : "#8b5cf6";

          const picUrl = selectedUser.pic
            ? (selectedUser.pic.startsWith("http") ? selectedUser.pic : `${BASE_API}${selectedUser.pic}`)
            : null;

          return (
            <div className="sud-container">
              {/* ✅ Hero Card */}
              <div className="sud-hero">
                <button className="sud-close-btn" onClick={() => setDrawerOpen(false)}>✕</button>

                <div className="sud-hero-bg"></div>

                <div className="sud-hero-content">
                  {/* Avatar */}
                  <div className="sud-avatar-wrap">
                    {picUrl ? (
                      <img src={picUrl} alt={fullName} className="sud-avatar-img"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className="sud-avatar-fallback" style={{ display: picUrl ? 'none' : 'flex' }}>
                      {initial}
                    </div>
                    {isActive && <div className="sud-online-dot"></div>}
                  </div>

                  {/* Name & ID */}
                  <h2 className="sud-name">{fullName}</h2>
                  <div className="sud-profile-id">{profileId}</div>

                  {/* Status Tags */}
                  <div className="sud-tags">
                    <span className={`sud-tag ${isActive ? 'active' : 'inactive'}`}>
                      {isActive ? '● Active' : '○ Inactive'}
                    </span>
                    <span className={`sud-tag ${isPremium ? 'premium' : 'free'}`}>
                      {isPremium ? '⭐ Premium' : '🆓 Free'}
                    </span>
                    <span className="sud-tag source" style={{ background: `${sourceColor}15`, color: sourceColor, borderColor: `${sourceColor}40` }}>
                      {sourceIcon} {source}
                    </span>
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="sud-quick-actions">
                    {selectedUser.contact && (
                      <>
                        <button className="sud-qa-btn call" onClick={() => handleCall(selectedUser.contact)}>
                          <PhoneOutlined /> Call
                        </button>
                        <button className="sud-qa-btn whatsapp" onClick={() => handleWhatsApp(selectedUser.contact)}>
                          <WhatsAppOutlined /> WhatsApp
                        </button>
                      </>
                    )}
                    {selectedUser.email && (
                      <button className="sud-qa-btn email" onClick={() => window.location.href = `mailto:${selectedUser.email}`}>
                        <MailOutlined /> Email
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ✅ Details Sections */}
              <div className="sud-body">

                {/* Personal Details */}
                <div className="sud-section">
                  <div className="sud-section-header">
                    <span className="sud-section-icon">👤</span>
                    <span className="sud-section-title">Personal Details</span>
                  </div>
                  <div className="sud-grid">
                    <div className="sud-item">
                      <div className="sud-item-icon"><UserOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Full Name</span>
                        <span className="sud-item-value">{fullName}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon"><UserOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Username</span>
                        <span className="sud-item-value">{selectedUser.username || "N/A"}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon">👤</div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Gender</span>
                        <span className="sud-item-value">
                          {selectedUser.gender === 'Male' ? '👨' : selectedUser.gender === 'Female' ? '👩' : ''} {capitalize(selectedUser.gender) || "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon">🎂</div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Age</span>
                        <span className="sud-item-value">{selectedUser.age ? `${selectedUser.age} Years` : "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="sud-section">
                  <div className="sud-section-header">
                    <span className="sud-section-icon">📞</span>
                    <span className="sud-section-title">Contact Details</span>
                  </div>
                  <div className="sud-grid">
                    <div className="sud-item clickable" onClick={() => handleCopy(selectedUser.contact, "Mobile")}>
                      <div className="sud-item-icon"><PhoneOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Mobile Number</span>
                        <span className="sud-item-value">{selectedUser.contact || "N/A"} <CopyOutlined className="sud-copy" /></span>
                      </div>
                    </div>
                    <div className="sud-item clickable" onClick={() => handleCopy(selectedUser.email, "Email")}>
                      <div className="sud-item-icon"><MailOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Email Address</span>
                        <span className="sud-item-value sud-email-text">{selectedUser.email || "N/A"} {selectedUser.email && <CopyOutlined className="sud-copy" />}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="sud-section">
                  <div className="sud-section-header">
                    <span className="sud-section-icon">📍</span>
                    <span className="sud-section-title">Location</span>
                  </div>
                  <div className="sud-grid">
                    <div className="sud-item">
                      <div className="sud-item-icon"><EnvironmentOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">District / City</span>
                        <span className="sud-item-value">{selectedUser.district || selectedUser.city || "N/A"}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon"><EnvironmentOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">State</span>
                        <span className="sud-item-value">{selectedUser.state || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Religion & Caste */}
                <div className="sud-section">
                  <div className="sud-section-header">
                    <span className="sud-section-icon">🕉️</span>
                    <span className="sud-section-title">Religion & Caste</span>
                  </div>
                  <div className="sud-grid">
                    <div className="sud-item">
                      <div className="sud-item-icon">🙏</div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Religion</span>
                        <span className="sud-item-value">{selectedUser.religion || "N/A"}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon">👥</div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Caste</span>
                        <span className="sud-item-value">{selectedUser.caste || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Info */}
                <div className="sud-section">
                  <div className="sud-section-header">
                    <span className="sud-section-icon">🔐</span>
                    <span className="sud-section-title">Account Information</span>
                  </div>
                  <div className="sud-grid">
                    <div className="sud-item">
                      <div className="sud-item-icon"><IdcardOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Profile ID</span>
                        <span className="sud-item-value sud-highlight">{profileId}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon"><CrownOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Plan</span>
                        <span className={`sud-item-value ${isPremium ? 'sud-premium' : 'sud-free'}`}>
                          {isPremium ? '⭐ Premium' : '🆓 Free Plan'}
                        </span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon"><WalletOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Balance</span>
                        <span className="sud-item-value sud-balance">{formatCurrency(selectedUser.balance || 0)}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon"><CalendarOutlined /></div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Joined</span>
                        <span className="sud-item-value">{formatDate(selectedUser.date_joined)}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon">{sourceIcon}</div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Source</span>
                        <span className="sud-item-value" style={{ color: sourceColor, fontWeight: 700 }}>{source}</span>
                      </div>
                    </div>
                    <div className="sud-item">
                      <div className="sud-item-icon">🔗</div>
                      <div className="sud-item-content">
                        <span className="sud-item-label">Refer Code</span>
                        <span className="sud-item-value">{selectedUser.refer || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          );
        })()}
      </Drawer>

      {/* ADD USER MODAL */}
      <Modal
        title={
          <div className="su-modal-title-new">
            <div className="su-modal-title-icon-wrap"><UserAddOutlined /></div>
            <div className="su-modal-title-text">
              <h3>Add New User</h3>
              <p>Create a complete user profile</p>
            </div>
          </div>
        }
        open={addModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={880}
        destroyOnClose
        className="su-add-modal"
        centered
      >
        {/* Staff Info Strip */}
        <div className="su-staff-strip">
          <div className="su-staff-badge">
            <div className="su-staff-badge-icon staff"><IdcardOutlined /></div>
            <div>
              <div className="su-staff-badge-label">Staff Member</div>
              <div className="su-staff-badge-value">{staffName} ({staffId || "STAFF"})</div>
            </div>
          </div>
          <div className="su-staff-badge">
            <div className="su-staff-badge-icon referral"><GiftOutlined /></div>
            <div>
              <div className="su-staff-badge-label">Referral Code</div>
              <div className="su-staff-badge-value">{staffRef || "N/A"}</div>
            </div>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUser}
          requiredMark={false}
          onFinishFailed={(errorInfo) => {
            if (errorInfo.outOfDate) return;
            if (errorInfo.errorFields.length > 0) {
              message.error(`Please fix ${errorInfo.errorFields.length} error(s)`);
              form.scrollToField(errorInfo.errorFields[0].name, { behavior: "smooth", block: "center" });
            }
          }}
        >
          {/* Photo Upload */}
          <div className="su-photo-section">
            <Upload beforeUpload={(f) => { setFile(f); setPreviewUrl(URL.createObjectURL(f)); return false; }} showUploadList={false}>
              <div className="su-photo-upload-new">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="su-photo-preview-new" />
                ) : (
                  <div className="su-photo-placeholder"><CameraOutlined /><span>Add Photo</span></div>
                )}
              </div>
            </Upload>
            <p className="su-photo-hint-new">📸 Upload a clear photo (optional)</p>
          </div>

          <div className="su-form-body">

            {/* ========== ACCOUNT INFO ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji account">🔐</div>
                <div>
                  <div className="su-section-title">Account Information</div>
                  <div className="su-section-subtitle">Login credentials for the user</div>
                </div>
              </div>
              <div className="su-form-grid-new">
                <Form.Item
                  name="username"
                  label={
                    <span>
                      <UserOutlined style={{ marginRight: 6, color: "#e91e63" }} />
                      Username <span className="su-required-star">*</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Username required" },
                    { min: 4, max: 16, message: "4-16 characters" },
                    { pattern: /^[a-z0-9]+$/, message: "Only lowercase & numbers" },
                  ]}
                >
                  <Input placeholder="e.g. rahul2024" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={
                    <span>
                      <LockOutlined style={{ marginRight: 6, color: "#e91e63" }} />
                      Password <span className="su-required-star">*</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Password required" },
                    { min: 8, message: "Min 8 characters" },
                    { pattern: /[A-Z]/, message: "One uppercase needed" },
                    { pattern: /[0-9]/, message: "One number needed" },
                  ]}
                >
                  <Input.Password
                    placeholder="Strong password"
                    autoComplete="new-password"
                    style={{
                      background: "white",
                    }}
                    styles={{
                      input: {
                        background: "transparent",
                        boxShadow: "none",
                      }
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="contact"
                  label={
                    <span>
                      <PhoneOutlined style={{ marginRight: 6, color: "#e91e63" }} />
                      Mobile <span className="su-required-star">*</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Mobile required" },
                    { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" },
                  ]}
                  normalize={(value) => value?.replace(/\D/g, '').slice(0, 10)}
                >
                  <Input placeholder="10-digit number" maxLength={10} inputMode="numeric" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={
                    <span>
                      <MailOutlined style={{ marginRight: 6, color: "#e91e63" }} />
                      Email
                    </span>
                  }
                  rules={[{ type: "email", message: "Invalid email" }]}
                >
                  <Input placeholder="user@email.com" />
                </Form.Item>
              </div>
            </div>

            {/* ========== PERSONAL INFO ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji personal">👤</div>
                <div>
                  <div className="su-section-title">Personal Details</div>
                  <div className="su-section-subtitle">Basic profile information</div>
                </div>
              </div>
              <div className="su-form-grid-new">

                {/* ✅ Full Name */}
                <Form.Item name="firstname" label="Full Name">
                  <Input placeholder="Enter full name" />
                </Form.Item>

                {/* ✅ Gender */}
                <Form.Item name="gender" label="Gender">
                  <AutoComplete
                    options={["Male", "Female", "Other", "Prefer not to say"].map(v => ({ value: v }))}
                    placeholder="Select or type gender"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                {/* ✅ DOB */}
                <Form.Item
                  name="dob"
                  label="Date of Birth"
                  rules={[{
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const dob = new Date(value);
                      const today = new Date();
                      let age = today.getFullYear() - dob.getFullYear();
                      const m = today.getMonth() - dob.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                      if (dob > today) return Promise.reject("Cannot be future date");
                      if (age < 18) return Promise.reject("Must be 18+ years old");
                      return Promise.resolve();
                    },
                  }]}
                >
                  <Input
                    type="date"
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
                  />
                </Form.Item>

                {/* ✅ Marital Status */}
                <Form.Item name="marrige_status" label="Marital Status" initialValue="Unmarried">
                  <AutoComplete
                    options={["Unmarried", "Divorced", "Widowed", "Separated", "Awaited"].map(v => ({ value: v }))}
                    placeholder="Select or type marital status"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                {/* ✅ Height */}
                <Form.Item name="height" label="Height">
                  <AutoComplete
                    options={heightOptions.map(v => ({ value: v }))}
                    placeholder="e.g. 5'6&quot; (168 cm)"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                {/* ✅ Blood Group */}
                <Form.Item name="blood_group" label="Blood Group">
                  <AutoComplete
                    options={bloodGroupOptions.map(v => ({ value: v }))}
                    placeholder="e.g. A+, B-, O+"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                {/* ✅ Mother Tongue */}
                <Form.Item name="mother_tongue" label="Mother Tongue">
                  <AutoComplete
                    options={motherTongueOptions.map(v => ({ value: v }))}
                    placeholder="e.g. Hindi, Gujarati..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                {/* ✅ Food Lifestyle */}
                <Form.Item name="food_lifestyle" label="Food Lifestyle">
                  <AutoComplete
                    options={["Vegetarian", "Non-Vegetarian", "Eggetarian", "Vegan", "Jain"].map(v => ({ value: v }))}
                    placeholder="e.g. Vegetarian, Vegan..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

              </div>
            </div>

            {/* ========== LOCATION ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji location">📍</div>
                <div>
                  <div className="su-section-title">Location Details</div>
                  <div className="su-section-subtitle">Where does the user live?</div>
                </div>
              </div>
              <div className="su-form-grid-new">

                {/* ✅ State */}
                <Form.Item name="state" label="State">
                  <AutoComplete
                    options={states.map(s => ({ value: s.state || s }))}
                    placeholder="Type or select state"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      const matchedState = states.find(s => (s.state || s) === value);
                      if (matchedState) handleStateChange(value);
                    }}
                  />
                </Form.Item>

                {/* ✅ District */}
                <Form.Item name="district" label="District">
                  <AutoComplete
                    options={districts.map(d => ({ value: d }))}
                    placeholder={districts.length ? "Type or select district" : "Select state first"}
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      const matched = districts.find(d => d === value);
                      if (matched) handleDistrictChange(value);
                    }}
                  />
                </Form.Item>

                {/* ✅ City */}
                <Form.Item name="city" label="City / Town">
                  <AutoComplete
                    options={cities.map(c => ({ value: c }))}
                    placeholder="Enter city name"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="country" label="Country" initialValue="India">
                  <Input />
                </Form.Item>

                <Form.Item name="contact1" label="Alternate Mobile">
                  <Input placeholder="Optional" maxLength={10} prefix={<PhoneOutlined />} />
                </Form.Item>

              </div>
            </div>

            {/* ========== RELIGION & CASTE ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji religion">🕉️</div>
                <div>
                  <div className="su-section-title">Religion & Caste</div>
                  <div className="su-section-subtitle">Religious background</div>
                </div>
              </div>
              <div className="su-form-grid-new">

                {/* ✅ Religion */}
                <Form.Item name="religion" label="Religion">
                  <AutoComplete
                    // ✅ HARDCODED LIST HATAO - API se loaded options use karo
                    options={religionOptions.map(r => ({
                      value: r.value || r,
                      label: `${getReligionIcon(r.value || r)} ${r.label || r.value || r}`,
                    }))}
                    placeholder="Select religion"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      const casteReligions = [
                        'Hindu', 'Muslim', 'MuslimShia', 'MuslimSunni',
                        'Christian', 'Sikh', 'Jain', 'JainDigambar',
                        'JainShwetambar', 'Buddhist', 'Parsi', 'Jewish',
                        'InterReligion', 'NoReligiousBelief'
                      ];
                      if (casteReligions.includes(value)) {
                        handleReligionChange(value);
                      } else {
                        setFilteredCastes([]);
                        form.setFieldsValue({ caste: undefined });
                      }
                    }}
                  />
                </Form.Item>

                {/* ✅ Caste */}
                <Form.Item name="caste" label="Caste">
                  <AutoComplete
                    options={filteredCastes.map(c => ({ value: c.name || c }))}
                    placeholder="Select religion first or type caste"
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="subcaste" label="Sub Caste">
                  <Input placeholder="Optional - type sub caste" />
                </Form.Item>

              </div>
            </div>

            {/* ========== FAMILY ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji family">👨‍👩‍👧‍👦</div>
                <div>
                  <div className="su-section-title">Family Details</div>
                  <div className="su-section-subtitle">Family background information</div>
                </div>
              </div>
              <div className="su-form-grid-new">

                <Form.Item name="father_name" label="Father's Name">
                  <Input placeholder="Enter father's name" />
                </Form.Item>
                {/* ✅ Father Occupation */}
                <Form.Item name="father_occupation" label="Father's Occupation">
                  <AutoComplete
                    options={fatherOccupationOptions.map(v => ({ value: v }))}  // 👈 fatherOccupationOptions use karein
                    placeholder="e.g. Farmer, Doctor, Business..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="mother_name" label="Mother's Name">
                  <Input placeholder="Enter mother's name" />
                </Form.Item>

                
                {/* ✅ Mother Occupation */}
                <Form.Item name="mother_occupation" label="Mother's Occupation">
                  <AutoComplete
                    options={occupationOptions.map(v => ({ value: v }))}
                    placeholder="e.g. Housewife, Teacher..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

              </div>

              {/* Siblings Grid */}
              <div className="su-number-grid" style={{ marginTop: 12 }}>
                <Form.Item name="brother" label="Brothers" initialValue={0}>
                  <Input type="number" min={0} max={20} />
                </Form.Item>

                <Form.Item
                  name="brother_marrige"
                  label="Married Bros"
                  initialValue={0}
                  dependencies={["brother"]}
                  rules={[({ getFieldValue }) => ({
                    validator(_, value) {
                      const married = Number(value) || 0;
                      const total = Number(getFieldValue("brother")) || 0;
                      if (married > total) return Promise.reject(`Max ${total}`);
                      return Promise.resolve();
                    },
                  })]}
                >
                  <Input type="number" min={0} />
                </Form.Item>

                <Form.Item name="sister" label="Sisters" initialValue={0}>
                  <Input type="number" min={0} max={20} />
                </Form.Item>

                <Form.Item
                  name="sister_marrige"
                  label="Married Sis"
                  initialValue={0}
                  dependencies={["sister"]}
                  rules={[({ getFieldValue }) => ({
                    validator(_, value) {
                      const married = Number(value) || 0;
                      const total = Number(getFieldValue("sister")) || 0;
                      if (married > total) return Promise.reject(`Max ${total}`);
                      return Promise.resolve();
                    },
                  })]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </div>
            </div>

            {/* ========== EDUCATION & CAREER ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji education">🎓</div>
                <div>
                  <div className="su-section-title">Education & Career</div>
                  <div className="su-section-subtitle">Qualifications & profession</div>
                </div>
              </div>
              <div className="su-form-grid-new">

                <Form.Item name="university" label="University / College">
                  <Input placeholder="Enter university name" />
                </Form.Item>

                {/* ✅ Course */}
                <Form.Item name="course" label="Highest Qualification">
                  <AutoComplete
                    options={courseOptions.map(v => ({ value: v }))}
                    placeholder="e.g. B.Tech, MBA, 12th..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                      if (subCourseData[value]) handleCourseChange(value);
                      else { setIsOtherCourse(true); setSubCourses([]); }
                    }}
                  />
                </Form.Item>

                {/* ✅ Sub Course */}
                <Form.Item name="sub_course" label="Specialization">
                  <AutoComplete
                    options={subCourses.map(v => ({ value: v }))}
                    placeholder="e.g. Computer Science, Finance..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="job_title" label="Job / Profession">
                  <Input placeholder="e.g. Software Engineer" />
                </Form.Item>

                {/* ✅ Job Type */}
                <Form.Item name="job_type" label="Employment Type">
                  <AutoComplete
                    options={["Government", "Private", "Self Employed", "Business", "Not Working", "Retired", "Student"].map(v => ({ value: v }))}
                    placeholder="e.g. Government, Private..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

                <Form.Item name="annual_income" label="Annual Income (₹)">
                  <Input type="number" placeholder="e.g. 500000" />
                </Form.Item>

                {/* ✅ Lifestyle */}
                <Form.Item name="Lifestyle" label="Lifestyle">
                  <AutoComplete
                    options={["Simple", "Moderate", "Rich", "Luxury", "Middle Class", "Upper Middle Class"].map(v => ({ value: v }))}
                    placeholder="e.g. Simple, Moderate..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

              </div>
            </div>

            {/* ========== ADDITIONAL ========== */}
            <div className="su-form-section">
              <div className="su-section-header">
                <div className="su-section-emoji additional">📝</div>
                <div>
                  <div className="su-section-title">Additional Information</div>
                  <div className="su-section-subtitle">Optional details</div>
                </div>
              </div>
              <div className="su-form-grid-new">

                {/* ✅ Profile Created By */}
                <Form.Item name="created_profile" label="Profile Created By">
                  <AutoComplete
                    options={["Self", "Parent", "Sibling", "Friend", "Staff", "Relative", "Other"].map(v => ({ value: v }))}
                    placeholder="e.g. Self, Parent, Sibling..."
                    allowClear
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>

              </div>

              {/* About Box */}
              <div className="su-about-box" style={{ marginTop: 12 }}>
                <div className="su-about-box-header">
                  <span className="su-about-box-label">📝 About the User</span>
                  <span className="su-about-box-badge">Optional</span>
                </div>
                <Form.Item name="additional_notes" style={{ marginBottom: 0 }}>
                  <Input.TextArea
                    maxLength={500}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    placeholder="Write something about this user..."
                    style={{ border: "none", boxShadow: "none", padding: 0, resize: "none" }}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* ========== FOOTER ========== */}
          <div className="su-form-footer">
            <div className="su-form-footer-left">
              <SafetyOutlined />
              <span>All data is securely stored</span>
            </div>
            <div className="su-form-footer-actions">
              <Button onClick={handleModalClose} className="su-cancel-btn">Cancel</Button>
              <Button type="primary" htmlType="submit" loading={addLoading} icon={<HeartFilled />} className="su-save-btn">
                Save User Profile
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default StaffUsers;